import type { RoomSummary } from '@brush/api-client';

/*
 * Overview's totals cards under the totals picker (docs/specs/user-preferences.md UP24, A5). Under a period the
 * Tournaments, Prize pool and Total entries cards SHOW the period's figures, each compared with the period before as
 * its hint, and never the all-time figure beside them. The Players card has no period form and the API is unchanged,
 * so it stays all-time under every period, labelled "Players (all time)" while one is set. "N live now" states the
 * present under every period. Under All time the four cards read exactly as they did before 1a.
 *
 * Whether a period is in force is read off the response (`period`), never off the picker: the cards then always say
 * what the figures beside them are, including while a new answer is on its way.
 */

export interface TotalsCard {
  label: string;
  value: string;
  hint?: string;
}

type Summary = Pick<RoomSummary, 'counts' | 'tournaments' | 'period'>;

/** A date with a named month, read in UTC so no zone moves it — `03/04/2026` is two days depending on the reader. */
const DAY = new Intl.DateTimeFormat('en-US', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });

function day(iso: string): string {
  const parsed = new Date(`${iso}T00:00:00Z`);
  return Number.isNaN(parsed.getTime()) ? iso : DAY.format(parsed);
}

/** A window by its dates; one day is one date, not a range of one. */
function range(from: string, to: string): string {
  return from === to ? day(from) : `${day(from)} – ${day(to)}`;
}

const money = (n: number) => `$${n.toLocaleString()}`;

/** The four cards, in their order: Tournaments, Prize pool, Total entries, Players. */
export function totalsCards(summary: Summary | undefined, players: number): TotalsCard[] {
  const live = summary?.tournaments.live ?? 0;
  const liveNow = live > 0 ? `${live} live now` : null;
  const period = summary?.period;

  if (!period) {
    return [
      { label: 'Tournaments', value: String(summary?.counts.tournaments ?? 0), ...(liveNow ? { hint: liveNow } : {}) },
      { label: 'Prize pool', value: money(summary?.tournaments.prizePool ?? 0) },
      { label: 'Total entries', value: (summary?.tournaments.entries ?? 0).toLocaleString() },
      { label: 'Players', value: players.toLocaleString() },
    ];
  }

  const before = range(period.previousFrom, period.previousTo);
  return [
    {
      label: 'Tournaments',
      value: String(period.tournaments),
      hint: [liveNow, `${period.previousTournaments} in ${before}`].filter(Boolean).join(' · '),
    },
    { label: 'Prize pool', value: money(period.prizePool), hint: `${money(period.previousPrizePool)} in ${before}` },
    {
      label: 'Total entries',
      value: period.entries.toLocaleString(),
      hint: `${period.previousEntries.toLocaleString()} in ${before}`,
    },
    { label: 'Players (all time)', value: players.toLocaleString() },
  ];
}

/** The period the totals cover, by its dates — the server's echo — or null under All time. */
export function totalsCaption(summary: Pick<RoomSummary, 'period'> | undefined): string | null {
  const period = summary?.period;
  return period ? range(period.from, period.to) : null;
}
